import {
  getFirebaseCustomToken,
  postLogin,
  type ApiResponse,
  type EmployeeLoginData
} from '@accumulus/shared';
import {auth,useAuthState} from '@accumulus/shared';
import Logo from '@/components/logo';
import {Button} from '@/components/ui/button';
import {Input, Label} from '@/components/ui';
import {getAuth, signInWithCustomToken} from 'firebase/auth';
import {Eye, EyeOff} from 'lucide-react';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {setEmployee} from '@/stores/employee';
import {toast} from 'sonner';

export default function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [user, loading] = useAuthState(auth);

  const onFinish = async () => {
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setIsLoading(true);
    try {
      const res: ApiResponse<EmployeeLoginData> = await postLogin(
        email,
        password
      );
      if (!res.success) {
        setIsLoading(false);
        toast.error('Invalid credentials');
        return;
      }
      localStorage.setItem('accessToken', res.data.access_token);
      localStorage.setItem('refreshToken', res.data.refresh_token);
      setEmployee(res.data.employee ?? null);

      const customToken = (await getFirebaseCustomToken()).data.custom_token;
      const firebaseAuth = getAuth();
      const userCred = await signInWithCustomToken(firebaseAuth, customToken);
      localStorage.setItem('token', userCred.user.uid);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.error(err);
      toast.error('Login failed');
    }
  };

  useEffect(() => {
    if (loading) return;
    if (user) {
      navigate('/', {replace: true});
    }
  }, [user, loading, navigate]);

  return (
    <div className="flex h-screen w-full items-center justify-center bg-primary-dark">
      <div className="w-[400px] rounded-lg border border-border bg-card p-6 shadow-md">
        <div className="flex justify-center pb-4">
          <Logo />
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onFinish()}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <button
                type="button"
                className="text-sm text-mountain-meadow underline-offset-4 hover:underline"
                onClick={() => navigate('/forgot-password')}
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onFinish()}
                className="pr-10"
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground focus:outline-none"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <Button
            className="w-full"
            isLoading={isLoading}
            onClick={onFinish}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Sign In'}
          </Button>
        </div>
      </div>
    </div>
  );
}
